'use client';

import { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Heading, Link, TextField } from '@invisionag/components';
import { useTranslation } from '@invisionag/next/i18n';
import { Result } from '@invisionag/next/result';
import { CenteredContainer } from '../../_components/centered-container';
import { Form, FormMessage, FormResult, SubmitButton } from '@/lib/form';
import * as Cortex from '@/repositories/cortex';

export function RequestPasswordResetForm() {
  const { t } = useTranslation();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [prefilledEmail] = useState<string | undefined>(
    searchParams.get('email') ?? undefined,
  );

  useEffect(() => {
    const filteredParams = new URLSearchParams(searchParams);
    if (filteredParams.has('email')) {
      filteredParams.delete('email');
      router.replace(`/request-password-reset?${filteredParams.toString()}`);
    }
  }, [router, searchParams]);

  async function requestPasswordReset(formData: FormData): Promise<FormResult> {
    const email = assertString(formData.get('email')).trim();

    if (!email) {
      return Result.Err({
        validationErrors: {
          email: t(
            'request-password-reset.form.field.email.validation.valueMissing',
          ),
        },
      });
    }

    await Cortex.requestPasswordReset(email);
    return Result.Ok({
      message: t('request-password-reset.form.success-message'),
    });
  }

  return (
    <CenteredContainer>
      <Heading level={1} className="text-center">
        {t('request-password-reset.form.heading')}
      </Heading>
      <Form className="grid gap-4" action={requestPasswordReset}>
        <div className="grid gap-4">
          <TextField
            isRequired
            autoFocus={true}
            label={t('request-password-reset.form.field.email.label')}
            name="email"
            type="email"
            defaultValue={prefilledEmail}
          />
        </div>
        <FormMessage />
        <SubmitButton className="w-full">
          {t('request-password-reset.form.submit')}
        </SubmitButton>
      </Form>

      <Link
        className="justify-self-center"
        href={`/login?${searchParams.toString()}`}
      >
        {t('request-password-reset.form.login-link')}
      </Link>
    </CenteredContainer>
  );
}

function assertString(value: unknown): string {
  if (typeof value === 'string') return value;
  throw new Error(`Expected string, got '${typeof value}'`);
}
